'use client';

import { clsx } from 'clsx';
import Link from 'next/link';
import React, { useState } from 'react';
import { appDirLogout } from '~/api/appDir/user';
import LockerLink from '~/components/Links/LockerLink';
import MyPurchasesLink from '~/components/Links/MyPurchasesLink';
import ReferralLink from '~/components/Links/ReferralLink';
import SellerDashboardLink from '~/components/Links/SellerDashboardLink';
import SettingsPageLink from '~/components/Links/SettingsPageLink';
import ValueGuideLink from '~/components/Links/ValueGuideLink';
import { useDelayFn } from '~/hooks/useDelayFn';
import { dispatchLogoutEvent } from '~/lib/events/customEvents';
import ReferralDollar from '~/public/static/svg/ReferralDollar';

interface Props {
  avatar: React.ReactNode;
  pro?: React.ReactNode;
  spendableBalance?: React.ReactNode;
  username?: string;
}

const AppDirAvatarSection: React.FC<Props> = ({
  avatar,
  pro,
  spendableBalance,
  username,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [callDelay, cancel] = useDelayFn(setOpen, 150);

  function handleClose() {
    cancel();
    setOpen(false);
  }

  function closeViaNavigation() {
    handleClose();
  }

  function handleOpen() {
    cancel();
    callDelay(true);
  }

  function handleMouseOut() {
    cancel();
    callDelay(false);
  }

  return (
    <span className="relative flex">
      <button
        type="button"
        onFocus={handleOpen}
        onBlur={handleClose}
        onMouseOver={handleOpen}
        onMouseOut={handleMouseOut}
        aria-controls="avatar-menu"
        aria-label="View account navigation"
        aria-haspopup={true}
        aria-expanded={open ? true : undefined}
      >
        {avatar}
      </button>

      <div
        className={clsx('nav-action-child right-0 mt-3', { open })}
        onFocus={() => {
          cancel();
          setOpen(true);
        }}
        onBlur={handleClose}
        onMouseOver={() => {
          cancel();
          setOpen(true);
        }}
        onMouseOut={handleMouseOut}
        style={{
          zIndex: 100,
        }}
      >
        <ul role="menu" id="avatar-menu" aria-hidden={!open}>
          <li>
            <LockerLink role="menuitem" username={username}>
              <b>@{username}</b>
            </LockerLink>
          </li>

          {spendableBalance}

          <li>
            <LockerLink role="menuitem" username={username}>
              My Locker
            </LockerLink>
          </li>

          <li>
            <Link href="/gift-cards" role="menuitem">
              Gift Cards
            </Link>
          </li>

          <li>
            <ValueGuideLink role="menuitem">Value Guide</ValueGuideLink>
          </li>

          <li>
            <ReferralLink
              page="invite"
              role="menuitem"
              style={{
                display: 'flex',
              }}
            >
              <div className="mr-2">Invite Friends</div>
              <div>
                <ReferralDollar />
              </div>
            </ReferralLink>
          </li>

          <li>
            <SellerDashboardLink role="menuitem">
              Seller Dashboard
            </SellerDashboardLink>
          </li>

          <li>
            <MyPurchasesLink role="menuitem">My Purchases</MyPurchasesLink>
          </li>

          {pro}

          <li>
            <SettingsPageLink page="account" role="menuitem">
              My Settings
            </SettingsPageLink>
          </li>

          <li>
            <SettingsPageLink page="vacation" role="menuitem">
              Vacation Mode
            </SettingsPageLink>
          </li>

          <li>
            <button
              type="button"
              onClick={() => {
                appDirLogout();
                dispatchLogoutEvent();
              }}
              className="block w-full text-left"
            >
              Logout
            </button>
          </li>
        </ul>
      </div>
    </span>
  );
};

export default AppDirAvatarSection;
